import { ThemeEvents, CartUpdateEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";
import { cartTakesFee, isGiftCardType, setCartShapeFromCart } from "@theme/cw-gift-card";

const CONFIG_SCRIPT_ID = "CartDonationConfig";
const SOURCE = "cart-donation";

/** Serialized product-form bundle helpers vs overlapping submits */
let appendDonationMutex = Promise.resolve();

/** Avoid appending the donation line into multiple overlapping add-to-cart requests */
let donationAddPendingUntil = 0;

/** Last-known donation line presence (updated whenever we parse /cart.js in sync) */
/** @type {{ known: boolean; present: boolean }} */
let donationPresence = { known: false, present: false };

function parseCartDonationConfigFromDom() {
  const configScript = document.getElementById(CONFIG_SCRIPT_ID);
  if (!configScript?.textContent) return null;

  try {
    return JSON.parse(configScript.textContent);
  } catch {
    return null;
  }
}

/** The products the shop charges no packaging fee for, out of the same config block. */
function getFeeExemptProductIdsFromConfig(config) {
  const ids = config?.feeExemptProductIds;
  return Array.isArray(ids) ? ids.map(Number).filter(Number.isFinite) : [];
}

/**
 * @param {Record<string, unknown> | null | undefined} config
 */
function getDonationVariantIdFromConfig(config) {
  if (!config?.variantId && config?.variantId !== 0) return null;
  const id = Number(config.variantId);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * @param {unknown} cart
 * @param {number} donationVariantId
 */
export function syncDonationPresenceFromCart(cart, donationVariantId) {
  const items = /** @type {{ variant_id?: number }[]} */ (cart?.items ?? []);
  const present = items.some(
    (item) => Number(item.variant_id) === donationVariantId,
  );
  donationPresence = { known: true, present };
  if (present) donationAddPendingUntil = 0;
}

/**
 * @param {FormData} formData
 * @param {number} donationVid
 */
function formAlreadyIncludesDonation(formData, donationVid) {
  const primary = formData.get("id");
  if (primary != null && Number(primary) === donationVid) return true;

  const extraIds = formData.getAll("items[][id]");
  return extraIds.some((v) => Number(v) === donationVid);
}

async function appendDonationToProductFormAddInner(formData, productType, productId) {
  const config = parseCartDonationConfigFromDom();
  const donationVid = getDonationVariantIdFromConfig(config);
  const feeExemptProductIds = getFeeExemptProductIdsFromConfig(config);
  if (!donationVid || config.enabled === false) return;

  const mainIdRaw = formData.get("id");
  const mainVid = mainIdRaw ? Number(mainIdRaw) : NaN;
  if (!Number.isFinite(mainVid) || mainVid === donationVid) return;

  if (formAlreadyIncludesDonation(formData, donationVid)) return;
  if (Date.now() < donationAddPendingUntil) {
    return;
  }

  let cart;
  try {
    const cartRes = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
    });
    if (!cartRes.ok) return;
    cart = await cartRes.json();
  } catch {
    return;
  }

  syncDonationPresenceFromCart(cart, donationVid);
  setCartShapeFromCart(cart, donationVid);
  if (donationPresence.present) {
    return;
  }

  // Nothing exempt brings the fee in by itself, so the fee only rides along when the cart
  // already holds something ordinary. Checked against the cart rather than against this page.
  const addedIsExempt =
    isGiftCardType(productType) ||
    feeExemptProductIds.some((id) => Number(id) === Number(productId));
  if (addedIsExempt && !cartTakesFee(cart, donationVid, feeExemptProductIds)) {
    return;
  }

  donationAddPendingUntil = Date.now() + 5000;
  formData.append("items[][id]", String(donationVid));
  formData.append("items[][quantity]", "1");
  formData.append("items[][properties][_cart_donation]", "true");
}

/**
 * Merge donation line into the same AJAX add as the main variant (Horizon upsell pattern).
 * @param {FormData} formData
 */
export function appendDonationToProductFormAdd(formData, productType, productId) {
  const task = appendDonationMutex.then(() =>
    appendDonationToProductFormAddInner(formData, productType, productId),
  );
  appendDonationMutex = task.catch(() => {});
  return task;
}

class CartDonation {
  /** Serialize sync passes so overlapping cart:update events cannot double-add */
  #syncChain = Promise.resolve();

  /** @type {number | null} */
  variantId = null;

  /** Selling plan / inventory: Liquid marks unavailable variants false — skip add to avoid 422 */
  canAddDonation = true;

  /** Products the shop charges no packaging fee for. */
  feeExemptProductIds = [];

  constructor() {
    const parsed = this.parseConfig();

    this.variantId = this.getVariantId(parsed);
    if (!this.variantId) {
      return;
    }

    this.canAddDonation = parsed?.enabled !== false;
    this.feeExemptProductIds = getFeeExemptProductIdsFromConfig(parsed);

    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    window.addEventListener("pageshow", this.handlePageShow);

    queueMicrotask(() => {
      this.enqueueSync();
    });
  }

  handlePageShow = (event) => {
    if (event.persisted) {
      this.enqueueSync();
    }
  };

  /** Re-read #CartDonationConfig so sellability matches latest Liquid after reload/editor saves */
  refreshConfigSellability() {
    const parsed = this.parseConfig();
    if (!parsed) return;

    const vid = this.getVariantId(parsed);
    if (vid) this.variantId = vid;
    this.canAddDonation = parsed.enabled !== false;
    this.feeExemptProductIds = getFeeExemptProductIdsFromConfig(parsed);
  }

  parseConfig() {
    return parseCartDonationConfigFromDom();
  }

  /**
   * @param {Record<string, unknown> | null} config
   */
  getVariantId(config = this.rawConfig) {
    return getDonationVariantIdFromConfig(config);
  }

  /**
   * @param {CartUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    const detail = event.detail?.data;

    if (detail?.source === SOURCE) return;
    if (detail?.didError) return;

    this.enqueueSync();
  };

  enqueueSync() {
    this.#syncChain = this.#syncChain
      .then(() => this.runSyncPass())
      .catch(() => {});
  }

  async runSyncPass() {
    try {
      this.refreshConfigSellability();

      const cart = await this.getCart();
      syncDonationPresenceFromCart(cart, this.variantId);
      setCartShapeFromCart(cart, this.variantId);

      const donationLine = cart.items.find(
        (item) => item.variant_id === this.variantId,
      );
      // Gift cards and campaign products carry no fee, so a cart of those alone is treated the
      // same as an empty one here.
      const takesFee = cartTakesFee(cart, this.variantId, this.feeExemptProductIds);

      if (donationLine && donationLine.quantity !== 1) {
        await this.setDonationQuantityToOne(donationLine);
        return;
      }

      // Removing the last real product used to leave the fee behind on its own.
      if (donationLine && !takesFee) {
        await this.removeDonation(donationLine);
        return;
      }

      if (takesFee && !donationLine) {
        if (this.canAddDonation) {
          await this.addDonation();
        }
      }
    } catch {}
  }

  async getCart() {
    const response = await fetch("/cart.js", {
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) throw new Error("Could not fetch cart");

    return response.json();
  }

  getSectionIds() {
    const sectionIds = new Set();

    document.querySelectorAll("cart-items-component").forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    return Array.from(sectionIds);
  }

  async addDonation() {
    const sectionIds = this.getSectionIds();

    const formData = new FormData();
    formData.append("id", String(this.variantId));
    formData.append("quantity", "1");
    formData.append("properties[_cart_donation]", "true");

    if (sectionIds.length > 0) {
      formData.append("sections", sectionIds.join(","));
      formData.append("sections_url", window.location.pathname);
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "application/json",
      },
    });

    const payload = await response.json();

    if (!response.ok || payload.status)
      throw new Error(payload.message || "Could not add donation product");

    this.dispatchCartUpdate(payload.sections || {});
  }

  /**
   * @param {{ key?: string; variant_id?: number; quantity?: number }} donationLine
   */
  /** Takes the fee back off a cart that no longer has anything to charge it on. */
  async removeDonation(donationLine) {
    const sectionIds = this.getSectionIds();

    const body = {
      id: donationLine.key || String(this.variantId),
      quantity: 0,
    };

    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(",");
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig("json", { body: JSON.stringify(body) }),
    );
    const payload = await response.json();

    if (!response.ok || payload.errors)
      throw new Error(payload.errors || "Could not remove the donation line");

    donationPresence = { known: true, present: false };
    this.dispatchCartUpdate(payload.sections || {});
  }

  async setDonationQuantityToOne(donationLine) {
    const sectionIds = this.getSectionIds();

    const body = {
      id: donationLine.key || String(this.variantId),
      quantity: 1,
    };

    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(",");
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig("json", { body: JSON.stringify(body) }),
    );
    const payload = await response.json();

    if (!response.ok || payload.errors)
      throw new Error(payload.errors || "Could not set donation quantity to 1");

    this.dispatchCartUpdate(payload.sections || {});
  }

  /**
   * @param {Record<string, string>} sections
   */
  dispatchCartUpdate(sections) {
    document.dispatchEvent(
      new CartUpdateEvent({}, SOURCE, {
        source: SOURCE,
        sections,
      }),
    );
  }
}

new CartDonation();
